// BillingTransactionContext.tsx

import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  ReactNode,
} from "react";

import {
  useBillingTransaction,
  useBillingTransactions,
} from "@/hooks/useBillingTransactions";

import {
  BillingPaymentMethod,
  BillingTransactionsFilters,
  BillingTransactionFilterType,
  BillingTransactionStatus,
} from "@/types/billing-transactions";

type SortField = "amount" | "type" | "status" | "paymentMethod" | "createdAt";
type SortDirection = "asc" | "desc" | null;

const toMinorCurrencyAmount = (amount: string): number | undefined => {
  const parsedAmount = Number(amount);

  if (!Number.isFinite(parsedAmount)) {
    return undefined;
  }

  return Math.round(parsedAmount * 100);
};

interface BillingTransactionContextType {
  useBillingTransactions: (
    page?: number,
    limit?: number,
    filters?: BillingTransactionsFilters,
  ) => ReturnType<typeof useBillingTransactions>;

  useBillingTransaction: (
    id: string,
  ) => ReturnType<typeof useBillingTransaction>;

  filters: {
    status: BillingTransactionStatus | "all statuses";
    type: BillingTransactionFilterType | "all types";
    paymentMethod: BillingPaymentMethod | "all methods";
    serviceId?: string;
    guestId?: string;
    fromDate: string;
    toDate: string;
    minAmount: string;
    maxAmount: string;
    currentPage: number;
    sortField: SortField | null;
    sortDirection: SortDirection;
    selectedTransactions: string[];
    hotelId?: string | "all hotels";
  };

  actions: {
    setStatus: (value: BillingTransactionStatus | "all statuses") => void;
    setType: (value: BillingTransactionFilterType | "all types") => void;
    setPaymentMethod: (value: BillingPaymentMethod | "all methods") => void;
    setServiceId: (value?: string) => void;
    setGuestId: (value?: string) => void;
    setFromDate: (value: string) => void;
    setToDate: (value: string) => void;
    setMinAmount: (value: string) => void;
    setMaxAmount: (value: string) => void;
    setCurrentPage: (page: number) => void;
    setSortField: (field: SortField | null) => void;
    setSortDirection: (dir: SortDirection) => void;
    setSelectedTransactions: (ids: string[]) => void;
    setHotelId: (hotelId?: string | "all hotels") => void;

    handleSort: (field: string) => void;
    handleFilterChange: (setter: (value: any) => void, value: any) => void;

    handleSelectTransaction: (id: string, checked: boolean) => void;
    handleSelectAll: (checked: boolean, ids: string[]) => void;

    resetFilters: () => void;
  };

  computed: {
    apiFilters: BillingTransactionsFilters;
    limit: number;
  };
}

const BillingTransactionContext = createContext<
  BillingTransactionContextType | undefined
>(undefined);

interface BillingTransactionProviderProps {
  children: ReactNode;
}

export const BillingTransactionProvider: React.FC<
  BillingTransactionProviderProps
> = ({ children }) => {
  const [status, setStatus] = useState<
    BillingTransactionStatus | "all statuses"
  >("all statuses");

  const [type, setType] = useState<BillingTransactionFilterType | "all types">(
    "all types",
  );
  const [paymentMethod, setPaymentMethod] = useState<
    BillingPaymentMethod | "all methods"
  >("all methods");

  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");
  const [serviceId, setServiceId] = useState<string | undefined>(undefined);
  const [guestId, setGuestId] = useState<string | undefined>(undefined);
  const [minAmount, setMinAmount] = useState<string>("");
  const [maxAmount, setMaxAmount] = useState<string>("");
  const [selectedHotel, setHotelId] = useState<
    string | "all hotels" | undefined
  >("all hotels");

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);

  const [selectedTransactions, setSelectedTransactions] = useState<string[]>(
    [],
  );

  const limit = 10;

  const handleSort = (field: string) => {
    const newSortField = field as SortField;

    if (sortField === newSortField) {
      let nextDirection: SortDirection;

      if (sortDirection === "asc") {
        nextDirection = "desc";
      } else if (sortDirection === "desc") {
        nextDirection = null;
      } else {
        nextDirection = "asc";
      }

      setSortDirection(nextDirection);

      if (nextDirection === null) {
        setSortField(null);
      }
    } else {
      setSortField(newSortField);
      setSortDirection("asc");
    }

    setCurrentPage(1);
  };

  const handleFilterChange = (setter: (value: any) => void, value: any) => {
    setter(value);
    setCurrentPage(1);
  };

  const handleSelectTransaction = (id: string, checked: boolean) => {
    setSelectedTransactions((prev) =>
      checked ? [...prev, id] : prev.filter((x) => x !== id),
    );
  };

  const handleSelectAll = (checked: boolean, ids: string[]) => {
    if (checked) setSelectedTransactions(ids);
    else setSelectedTransactions([]);
  };

  const resetFilters = () => {
    setStatus("all statuses");
    setType("all types");
    setPaymentMethod("all methods");
    setFromDate("");
    setToDate("");
    setServiceId(undefined);
    setGuestId(undefined);
    setMinAmount("");
    setMaxAmount("");
    setCurrentPage(1);
    setSortField(null);
    setSortDirection(null);
    setSelectedTransactions([]);
    setHotelId("all hotels");
  };

  const apiFilters: BillingTransactionsFilters = useMemo(() => {
    const filters: BillingTransactionsFilters = {};

    if (status !== "all statuses") filters.status = status;
    if (type !== "all types") filters.type = type;
    if (paymentMethod !== "all methods") filters.paymentMethod = paymentMethod;

    if (fromDate) filters.fromDate = fromDate;
    if (toDate) filters.toDate = toDate;
    if (serviceId) filters.serviceId = serviceId;
    if (guestId) filters.guestId = guestId;
    if (selectedHotel && selectedHotel !== "all hotels")
      filters.hotelId = selectedHotel;

    const minAmountMinor = toMinorCurrencyAmount(minAmount);
    const maxAmountMinor = toMinorCurrencyAmount(maxAmount);

    if (minAmount !== "" && minAmountMinor !== undefined) {
      filters.minAmount = minAmountMinor;
    }
    if (maxAmount !== "" && maxAmountMinor !== undefined) {
      filters.maxAmount = maxAmountMinor;
    }

    if (sortField && sortDirection) {
      // @ts-expect-error якщо BillingTransactionsFilters
      filters.sort = `${sortField}:${sortDirection}`;
    }

    filters.page = currentPage;
    filters.limit = limit;

    return filters;
  }, [
    status,
    type,
    paymentMethod,
    fromDate,
    toDate,
    serviceId,
    guestId,
    minAmount,
    maxAmount,
    sortField,
    sortDirection,
    currentPage,
    limit,
    selectedHotel,
  ]);

  const contextValue: BillingTransactionContextType = {
    useBillingTransactions,
    useBillingTransaction,

    filters: {
      status,
      type,
      paymentMethod,
      serviceId,
      guestId,
      fromDate,
      toDate,
      minAmount,
      maxAmount,
      currentPage,
      sortField,
      sortDirection,
      selectedTransactions,
      hotelId: selectedHotel,
    },

    actions: {
      setHotelId,
      setStatus,
      setType,
      setPaymentMethod,
      setServiceId,
      setGuestId,
      setFromDate,
      setToDate,
      setMinAmount,
      setMaxAmount,
      setCurrentPage,
      setSortField,
      setSortDirection,
      setSelectedTransactions,

      handleSort,
      handleFilterChange,
      handleSelectTransaction,
      handleSelectAll,
      resetFilters,
    },

    computed: {
      apiFilters,
      limit,
    },
  };

  return (
    <BillingTransactionContext.Provider value={contextValue}>
      {children}
    </BillingTransactionContext.Provider>
  );
};

export const useBillingTransactionContext = () => {
  const context = useContext(BillingTransactionContext);
  if (!context) {
    throw new Error(
      "useBillingTransactionContext must be used within a BillingTransactionProvider",
    );
  }
  return context;
};
